import type {
  DeeplinkCategory,
  DeeplinkPreset,
} from '../interfaces/deeplinkCatalog'

/**
 * Filters deeplink presets by search query and category.
 */
export function filterDeeplinkPresets(
  presets: DeeplinkPreset[],
  searchQuery: string,
  selectedCategory: DeeplinkCategory | string,
): DeeplinkPreset[] {
  const query = searchQuery.trim().toLowerCase()
  const filterCategory = selectedCategory.trim()

  return presets.filter((preset) => {
    // Category match
    if (filterCategory && filterCategory !== 'All') {
      if (preset.category !== filterCategory) {
        return false
      }
    }

    // Search query match across name, scheme, and category
    if (query) {
      const nameMatch = preset.name.toLowerCase().includes(query)
      const schemeMatch = preset.scheme_url.toLowerCase().includes(query)
      const categoryMatch = preset.category.toLowerCase().includes(query)

      if (!nameMatch && !schemeMatch && !categoryMatch) {
        return false
      }
    }

    return true
  })
}

/**
 * Normalizes a URL/scheme string for comparisons (strips trailing slashes except scheme://).
 */
function normalizeDeeplink(val: string): string {
  return val
    .trim()
    .toLowerCase()
    .replace(/(?<!:)\/+$/, '')
}

/**
 * Checks whether a given deeplink URL or scheme exists in the provided presets.
 */
export function isDeeplinkInPresets(
  url: string | null | undefined,
  presets: DeeplinkPreset[],
): boolean {
  if (!url || typeof url !== 'string') return false
  const trimmed = url.trim().toLowerCase()
  if (!trimmed) return false

  const normalizedUrl = normalizeDeeplink(trimmed)
  return presets.some((p) => normalizeDeeplink(p.scheme_url) === normalizedUrl)
}

/**
 * Maps a habit category to a valid DeeplinkCategory for presets.
 */
export function mapHabitCategoryToDeeplinkCategory(
  habitCategory?: string,
): DeeplinkCategory {
  if (!habitCategory) return 'Productivity & Notes'
  const normalized = habitCategory.trim().toLowerCase()
  if (normalized.includes('health') || normalized.includes('fit'))
    return 'Fitness & Health'
  if (normalized.includes('learn') || normalized.includes('study'))
    return 'Learning'
  if (normalized.includes('read') || normalized.includes('book'))
    return 'Reading'
  if (normalized.includes('audio') || normalized.includes('music'))
    return 'Audio & Media'
  if (
    normalized.includes('prod') ||
    normalized.includes('note') ||
    normalized.includes('work') ||
    normalized.includes('personal')
  )
    return 'Productivity & Notes'
  return 'General'
}
